import { useEffect, useState } from "react";
import ContactDialog from "../components/ContactDialog";
import ProjectRequestDialog from "../components/ProjectRequestDialog";

export default function Landing() {
  const [contactOpen, setContactOpen] = useState(false);
  const [projectOpen, setProjectOpen] = useState(false);
  const [defaultService, setDefaultService] = useState("ersteinschaetzung");

  useEffect(() => {
    document.body.classList.add("letsbau");
    return () => document.body.classList.remove("letsbau");
  }, []);

  const openProject = (service) => {
    setDefaultService(service || "ersteinschaetzung");
    setProjectOpen(true);
  };

  return (
    <>
      {/* SEO context (visually hidden) — keeps a single visible H1 below */}
      <div className="sr-only" aria-hidden="false">
        <p>
          LET'S BAU – Baugesuch, Bewilligung und Bauberatung im Kanton Glarus.
          Persönliche Unterstützung bei Bauprojekten, Baugesuchen und der Kommunikation mit den
          Behörden im Glarnerland (Oberurnen, Glarus Nord, Glarus, Glarus Süd, Näfels, Netstal) –
          von der ersten Einschätzung bis zur Bewilligung.
        </p>
      </div>

      <header className="site-header" role="banner">
        <div className="container header-inner header-split">
          <nav className="nav nav-left" aria-label="Hauptnavigation links">
            <a href="#leistungen" data-testid="nav-leistungen">Leistungen</a>
            <a href="#idee" data-testid="nav-idee">Über uns</a>
          </nav>
          <a className="logo logo-center" href="#top" data-testid="site-logo" aria-label="LET'S BAU – Startseite">
            <img
              src={`${process.env.PUBLIC_URL}/images/logo.png`}
              alt="LET'S BAU – Baugesuch und Bauberatung im Kanton Glarus"
              width="220"
              height="148"
            />
          </a>
          <nav className="nav nav-right" aria-label="Hauptnavigation rechts">
            <a href="#kontakt" data-testid="nav-kontakt">Kontakt</a>
            <a href="#impressum" data-testid="nav-impressum">Impressum</a>
          </nav>
        </div>
      </header>

      <main id="top" role="main">
        <section className="hero" aria-labelledby="hero-title">
          <div className="container">
            <div className="hero-card" data-testid="hero-card" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/hero.jpg)` }} role="img" aria-label="Kanton Glarus mit Baupläne und Helm – LET'S BAU Bauberatung">
              <div className="hero-content">
                <h1 id="hero-title"><span className="h1-line">Von der Idee</span><span className="h1-line">zur Bewilligung</span></h1>
                <div className="eyebrow eyebrow-below">Bauberatung, Planung und Begleitung</div>
                <div className="cta-row">
                  <a className="lbtn primary" href="#leistungen" data-testid="hero-leistungen-btn">Leistungen ansehen</a>
                  <button className="lbtn" onClick={() => openProject()} data-testid="hero-project-btn">Projekt anfragen</button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="banner-section" aria-label="Kostenlose Erstberatung">
          <div className="container">
            <div className="offer-banner" data-testid="offer-banner">
              <div className="offer-banner-left">
                <div className="offer-banner-icon" aria-hidden="true">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 12 20 22 4 22 4 12"></polyline>
                    <rect x="2" y="7" width="20" height="5"></rect>
                    <line x1="12" y1="22" x2="12" y2="7"></line>
                    <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"></path>
                    <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"></path>
                  </svg>
                </div>
                <div className="offer-banner-text">
                  <p className="offer-banner-title">ERSTBERATUNG KOSTENLOS</p>
                  <p className="offer-banner-sub">Die erste Einschätzung Ihres Bauvorhabens ist unverbindlich und kostenlos.</p>
                </div>
              </div>

              <div className="offer-banner-middle">
                <div className="offer-banner-check" aria-hidden="true">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="9 12 11 14 15 10"></polyline>
                  </svg>
                </div>
                <div className="offer-banner-middle-text">
                  <p>Unverbindlich. Persönlich. Kompetent.</p>
                  <p>Wir nehmen uns Zeit für Ihr Projekt.</p>
                </div>
              </div>

              <button
                className="lbtn primary offer-banner-btn"
                onClick={() => setContactOpen(true)}
                data-testid="offer-banner-btn"
              >
                Jetzt Beratung anfragen
              </button>
            </div>
          </div>
        </section>

        <section id="ablauf">
          <div className="container">
            <h2 className="section-title">Prüfung. Planung. Einreichung.</h2>
            <div className="gallery-grid">
              <article className="gallery-card" data-testid="gallery-pruefung">
                <img
                  src={`${process.env.PUBLIC_URL}/images/step-1.jpg`}
                  alt="Prüfung und Beratung Baugesuch im Kanton Glarus – LET'S BAU"
                  loading="lazy"
                  decoding="async"
                />
                <div className="gallery-copy">
                  <h3>Prüfung & Beratung</h3>
                  <p>
                    Wir prüfen Ihr Bauvorhaben und zeigen Ihnen klar, was möglich ist. Sie erhalten eine verständliche Einschätzung und konkrete Empfehlungen für das weitere Vorgehen.
                  </p>
                </div>
              </article>
              <article className="gallery-card" data-testid="gallery-planung">
                <img
                  src={`${process.env.PUBLIC_URL}/images/step-2.jpg`}
                  alt="Planung und Vorbereitung Baugesuch Kanton Glarus – LET'S BAU"
                  loading="lazy"
                  decoding="async"
                />
                <div className="gallery-copy">
                  <h3>Planung & Vorbereitung</h3>
                  <p>
                    Wir unterstützen Sie bei der Ausarbeitung und bereiten alle notwendigen Unterlagen für das Baugesuch vor – strukturiert und vollständig.
                  </p>
                </div>
              </article>
              <article className="gallery-card" data-testid="gallery-einreichung">
                <img
                  src={`${process.env.PUBLIC_URL}/images/step-3.jpg`}
                  alt="Einreichung und Begleitung Baugesuch und Bewilligung Glarus – LET'S BAU"
                  loading="lazy"
                  decoding="async"
                />
                <div className="gallery-copy">
                  <h3>Einreichung & Begleitung</h3>
                  <p>
                    Wir reichen das Baugesuch ein, übernehmen die Kommunikation mit den Behörden und begleiten Sie bis zur Bewilligung – inklusive Rückfragen und Nachforderungen.
                  </p>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section id="leistungen">
          <div className="container">
            <h2 className="section-title">Unsere Leistungen</h2>
            <div className="services-grid">
              <article className="service-card" data-testid="service-ersteinschaetzung">
                <div>
                  <div className="service-index">01</div>
                  <h3>Ersteinschätzung</h3>
                  <ul>
                    <li>Erste Einschätzung Ihres Bauvorhabens</li>
                    <li>Prüfung der Umsetzbarkeit</li>
                    <li>Hinweise zu Risiken und Chancen</li>
                    <li>Klare nächste Schritte</li>
                  </ul>
                </div>
                <div>
                  <div className="price">CHF 150.–*</div>
                  <button className="lbtn primary mt-4" onClick={() => openProject("ersteinschaetzung")} data-testid="service-ersteinschaetzung-btn">Jetzt buchen</button>
                </div>
              </article>

              <article className="service-card" data-testid="service-bauermittlung">
                <div>
                  <div className="service-index">02</div>
                  <h3>Bauermittlung</h3>
                  <ul>
                    <li>Prüfung Ihrer bestehenden Unterlagen</li>
                    <li>Kontrolle auf Vollständigkeit und Plausibilität</li>
                    <li>Hinweise zu Optimierungen</li>
                    <li>Einschätzung zur Einreichungsfähigkeit</li>
                  </ul>
                </div>
                <div>
                  <div className="price">CHF 390.–*</div>
                  <button className="lbtn primary mt-4" onClick={() => openProject("bauermittlung")} data-testid="service-bauermittlung-btn">Jetzt buchen</button>
                </div>
              </article>

              <article className="service-card" data-testid="service-komplettbegleitung">
                <div>
                  <div className="service-index">03</div>
                  <h3>Komplettbegleitung</h3>
                  <ul>
                    <li>Beratung von der Idee bis zur Bewilligung</li>
                    <li>Unterstützung bei Planung und Unterlagen</li>
                    <li>Einreichung bei der Baubehörde</li>
                    <li>Kommunikation mit Behörden</li>
                    <li>Begleitung bei Rückfragen und Nachforderungen</li>
                  </ul>
                </div>
                <div>
                  <div className="price">Individuell*</div>
                  <button className="lbtn primary mt-4" onClick={() => openProject("komplettbegleitung")} data-testid="service-komplettbegleitung-btn">Anfrage stellen</button>
                </div>
              </article>
            </div>
            <p className="price-note" data-testid="price-note">
              * Preise können je nach Aufwand und Projektumfang variieren.
              <br />
              Zusätzliche Leistungen werden nach Aufwand verrechnet.
            </p>
          </div>
        </section>

        <section id="kontakt">
          <div className="container">
            <div className="cta-panel">
              <h2 className="section-title">Individuelle Anfrage</h2>
              <p>Für besondere Projekte, Spezialfälle oder eine persönliche Einschätzung.</p>
              <div className="cta-row" style={{ justifyContent: "center", marginTop: 24 }}>
                <button className="lbtn primary" onClick={() => setContactOpen(true)} data-testid="cta-contact-btn">Kontakt aufnehmen</button>
                <button className="lbtn" onClick={() => openProject()} data-testid="cta-project-btn">Projekt anfragen</button>
              </div>
            </div>
          </div>
        </section>

        <section id="idee" className="idee-section">
          <div className="container">
            <article className="idee-card" data-testid="idee-card">
              <div className="eyebrow eyebrow-below idee-eyebrow">Die Idee</div>
              <h2 className="idee-title">Die Idee hinter LET'S BAU</h2>
              <div className="idee-body">
                <p>
                  Bauprojekte und Baugesuche werden heute immer komplexer. Viele Anforderungen von Behörden und Fachstellen sind für Bauherren oft nicht direkt ersichtlich. Was viele nicht wissen: Bereits kleine Veränderungen an einer Liegenschaft – wie beispielsweise Anpassungen im Aussenbereich, Einfahrten, Zäune oder bauliche Änderungen am Gebäude – können bewilligungspflichtig sein.
                </p>
                <p>
                  In der Praxis führt das häufig zu Unsicherheiten, unvollständigen Unterlagen und Verzögerungen im Bewilligungsprozess. Rückfragen, Nachforderungen oder Sistierungen sind keine Seltenheit und verlängern die Verfahren zusätzlich – gerade auch aufgrund der hohen Auslastung der Behörden.
                </p>
                <p className="idee-highlight">Genau hier unterstützen wir.</p>
                <p>
                  Wir helfen Ihnen dabei, Ihr Bauvorhaben von Anfang an richtig aufzusetzen – verständlich, strukturiert und den Anforderungen entsprechend. Durch unsere Erfahrung im Umgang mit den Vorgaben im Kanton Glarus wissen wir, worauf es ankommt und wie Baugesuche effizient vorbereitet und eingereicht werden.
                </p>
                <p>
                  Dabei bieten wir keine Standardlösung, sondern eine individuell angepasste Begleitung – je nach Ausgangslage und Bedarf. Ob erste Einschätzung, gezielte Unterstützung oder komplette Begleitung bis zur Bewilligung: Wir stehen Ihnen zuverlässig zur Seite und übernehmen auf Wunsch auch die Kommunikation mit den zuständigen Behörden.
                </p>
                <p>
                  Unser Ziel ist es, den Prozess für Sie einfacher zu machen und die Voraussetzungen für eine reibungslose und möglichst zügige Bewilligung zu schaffen.
                </p>
              </div>
            </article>
          </div>
        </section>
      </main>

      <footer className="footer" id="impressum">
        <div className="container footer-inner">
          <div className="footer-left" data-testid="footer-impressum">
            <div className="footer-impressum-text">
              <p className="footer-impressum-heading">Impressum</p>
              <p>LET'S BAU · Bauberatung, Planung und Begleitung</p>
              <p>Neuquartierstrasse 2, 8868 Oberurnen · Kanton Glarus, Schweiz</p>
              <p><a href="mailto:lets.bau.glarus@gmail.com">lets.bau.glarus@gmail.com</a></p>
            </div>
          </div>
          <div className="footer-right">
            <p>© {new Date().getFullYear()} LET'S BAU</p>
            <img
              className="footer-right-logo"
              src={`${process.env.PUBLIC_URL}/images/logo.png`}
              alt="LET'S BAU – Bauberatung Kanton Glarus"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </footer>

      <ContactDialog open={contactOpen} onOpenChange={setContactOpen} />
      <ProjectRequestDialog open={projectOpen} onOpenChange={setProjectOpen} defaultService={defaultService} />
    </>
  );
}
